import { useEffect } from 'react';

/**
 * Body scroll-lock store.
 *
 * Multiple overlays can be open at the same time (e.g. a modal that opens
 * another modal). We reference-count the lock so we only restore the page
 * scroll when the LAST overlay closes.
 *
 * Technique:
 *   1. Read current scrollY before locking.
 *   2. Apply `position: fixed; top: -scrollY` on <body> — this is the only
 *      cross-browser way (especially on iOS Safari) to truly freeze the page
 *      while still allowing the modal's own internal scroll to work.
 *   3. Also lock <html> overflow + compensate scrollbar gutter so the layout
 *      doesn't jump.
 *   4. Pause Lenis smooth-scroll if present (it animates on window
 *      independently of body overflow).
 *   5. On unlock, undo everything and scrollTo the preserved Y.
 */

let lockCount = 0;
let saved = null; // { scrollY, htmlOverflow, bodyStyles, lenisRunning }

function applyLock() {
  if (typeof document === 'undefined') return;
  if (lockCount > 0) {
    lockCount += 1;
    return;
  }
  const html = document.documentElement;
  const body = document.body;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const scrollbarWidth = window.innerWidth - html.clientWidth;

  saved = {
    scrollY,
    htmlOverflow: html.style.overflow,
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    bodyLeft: body.style.left,
    bodyRight: body.style.right,
    bodyWidth: body.style.width,
    bodyOverflow: body.style.overflow,
    bodyPaddingRight: body.style.paddingRight,
  };

  // Compensate the disappearing scrollbar
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }

  // Freeze the page visually while keeping its scrollY in CSS top
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  html.style.overflow = 'hidden';

  // Pause Lenis (smooth-scroll engine)
  const lenis = typeof window !== 'undefined' ? window.__lenis : null;
  if (lenis && typeof lenis.stop === 'function') {
    saved.lenisWasRunning = true;
    lenis.stop();
  }

  lockCount = 1;
}

function releaseLock() {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount > 0) return;

  const html = document.documentElement;
  const body = document.body;

  if (saved) {
    body.style.position = saved.bodyPosition;
    body.style.top = saved.bodyTop;
    body.style.left = saved.bodyLeft;
    body.style.right = saved.bodyRight;
    body.style.width = saved.bodyWidth;
    body.style.overflow = saved.bodyOverflow;
    body.style.paddingRight = saved.bodyPaddingRight;
    html.style.overflow = saved.htmlOverflow;

    // Restore scroll position
    window.scrollTo({ top: saved.scrollY, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });

    // Resume Lenis
    const lenis = typeof window !== 'undefined' ? window.__lenis : null;
    if (lenis && saved.lenisWasRunning && typeof lenis.start === 'function') {
      lenis.start();
    }
    saved = null;
  }
}

/** React hook: pass a boolean. Lock is reference-counted (safe to nest). */
export const useBodyScrollLock = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;
    applyLock();
    return () => releaseLock();
  }, [locked]);
};

/** Imperative helpers (used by GlobalModalScrollLock observer) */
export const lockBodyScroll = applyLock;
export const unlockBodyScroll = releaseLock;
export const getLockCount = () => lockCount;

/**
 * Force-release ALL active locks AND scrub any inline lock styles from
 * <html>/<body>. Used as a route-change safety net so a stuck lock from an
 * unmounted modal can never freeze the page.
 */
export const forceUnlockAll = () => {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  const body = document.body;
  // Drop ref counter & saved state
  lockCount = 0;
  if (saved) {
    // Restore styles if we had them saved
    try { window.scrollTo({ top: saved.scrollY || 0, behavior: 'instant' in window ? 'instant' : 'auto' }); } catch (_) {}
    const lenis = typeof window !== 'undefined' ? window.__lenis : null;
    if (lenis && saved.lenisWasRunning && typeof lenis.start === 'function') lenis.start();
    saved = null;
  }
  // Always scrub any inline lock styles that may have been left behind
  ['position', 'top', 'left', 'right', 'width', 'overflow', 'paddingRight'].forEach((k) => {
    if (body.style[k]) body.style[k] = '';
  });
  if (html.style.overflow) html.style.overflow = '';
};

export default useBodyScrollLock;
